import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../../../store/store';
import { useGetCreditAssessmentReport, useSaveCreditAssessmentReport } from '../../../services/loanService';
import { formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';
import { useRecognisedPercent } from '../../../services/collateralSettings';
import {
  CONDITIONS_PRECEDENT, DECISIONS, DECISION_LABELS, OUTCOMES, OUTCOME_LABELS, OVERALL_LABELS, Outcome,
  ReportField, ReportSection, ReportValues, SECTIONS, conditionKey, consistencyIssues, evidenceStatus,
  overallResult, prefillFromApplication,
} from './creditReportModel';
import CreditAssessmentTemplateReport, { ReportRecommendation } from './CreditAssessmentTemplateReport';
import { TEMPLATE_VERSION } from './creditAssessmentTemplate';

const money = (n?: number) =>
  n === undefined ? '—' : n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });

const OUTCOME_CLASS: Record<string, string> = {
  PASS: 'bg-success', PASS_WITH_MITIGATION: 'bg-info text-dark', FAIL: 'bg-danger', HARD_STOP: 'bg-dark', NA: 'bg-secondary',
};
const OVERALL_CLASS: Record<string, string> = {
  INCOMPLETE: 'bg-secondary', HARD_STOP: 'bg-dark', FAIL: 'bg-danger', PASS_WITH_MITIGATION: 'bg-info text-dark', PASS: 'bg-success',
};

/**
 * The Credit Assessment Report (finding #5): the business's credit procedure (Credit Assessment Procedure
 * Manual rev01 / Report Template rev0) as a working report on the application. It is saved as a whole
 * and records who saved it; it does not yet drive the decision below it.
 */
const LegacyCreditAssessmentReport: React.FC<{ loanApplication: any }> = ({ loanApplication }) => {
  const applicationId: string = loanApplication?.id || '';
  const user = useSelector((state: RootState) => state.auth.user);
  const assessor = user?.name || user?.username || '';
  const { data: report, isLoading, refetch } = useGetCreditAssessmentReport(applicationId, { query: { enabled: !!applicationId } });
  const save = useSaveCreditAssessmentReport();
  const recognisedPercent = useRecognisedPercent();
  const [values, setValues] = useState<ReportValues>({});
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string>('pre');

  // The saved report, or one started from the application
  useEffect(() => {
    if (!report || dirty) return;
    const saved = (report.content || {}) as ReportValues;
    // A new report takes today's recognised share of a vehicle's retail value (Settings, finding #29)
    setValues(report.exists ? saved : { ...prefillFromApplication(loanApplication, assessor), 's7.recognisedPercent': recognisedPercent });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const set = (key: string, value: string | number | boolean | undefined) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const result = useMemo(() => overallResult(values), [values]);
  const issues = useMemo(() => consistencyIssues(values), [values]);
  const evidence = useMemo(() => evidenceStatus(values), [values]);

  const onSave = () => {
    save.mutate(
      {
        loanApplicationId: applicationId,
        data: {
          content: values as any,
          recommendation: (values['s11.decision'] as string) || undefined,
          overallResult: result,
          ...(assessor ? { savedBy: assessor } : {}),
        },
      },
      {
        onSuccess: () => { toast.success('Credit assessment report saved'); setDirty(false); refetch(); },
        onError: (e: any) => toast.error(e?.response?.data?.message || 'The report could not be saved'),
      },
    );
  };

  const input = (key: string, field: ReportField) => {
    const id = `car-${key}`;
    const value = values[key];
    if (field.kind === 'select') {
      return (
        <select id={id} className="form-select form-select-sm" value={(value as string) || ''} onChange={e => set(key, e.target.value || undefined)}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.kind === 'longtext') {
      return <textarea id={id} rows={2} className="form-control form-control-sm" value={(value as string) || ''} onChange={e => set(key, e.target.value)} />;
    }
    return (
      <input id={id} type={field.kind === 'money' ? 'number' : field.kind === 'date' ? 'date' : 'text'} step={field.kind === 'money' ? '0.01' : undefined}
        className="form-control form-control-sm" value={value === undefined ? '' : String(value)}
        onChange={e => set(key, field.kind === 'money' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)} />
    );
  };

  const sectionBadge = (s: ReportSection) => {
    if (!s.outcome) return null;
    const o = values[`${s.key}.outcome`] as Outcome | undefined;
    return o
      ? <span className={`badge ${OUTCOME_CLASS[o]} ms-2`}>{OUTCOME_LABELS[o]}</span>
      : <span className="badge bg-light text-muted border ms-2">No outcome</span>;
  };

  const renderSection = (s: ReportSection) => (
    <div className="p-3">
      {s.question && <p className="small text-muted fst-italic mb-3">{s.question}</p>}
      {s.rows && s.rowFields && (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle mb-0">
            <thead className="small text-muted"><tr><th></th>{s.rowFields.map(f => <th key={f.key}>{f.label}</th>)}</tr></thead>
            <tbody>
              {s.rows.map(r => (
                <tr key={r.key}>
                  <td className="small fw-semibold" style={{ minWidth: 180 }}>{r.label}</td>
                  {s.rowFields!.map(f => <td key={f.key} style={{ minWidth: f.kind === 'select' ? 150 : 170 }}>{input(`${s.key}.${r.key}.${f.key}`, f)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {s.fields && (
        <div className="row g-2">
          {s.fields.map(f => (
            <div key={f.key} className={f.kind === 'longtext' ? 'col-12' : 'col-md-6'}>
              <label htmlFor={`car-${s.key}.${f.key}`} className="form-label small mb-1">{f.label}</label>
              {input(`${s.key}.${f.key}`, f)}
              {f.hint && <div className="form-text small">{f.hint}</div>}
            </div>
          ))}
        </div>
      )}
      {s.key === 's11' && (
        <div className="mt-3">
          <div className="small fw-semibold mb-1">11.8 Conditions precedent</div>
          <div className="d-flex flex-wrap gap-3">
            {CONDITIONS_PRECEDENT.map(c => (
              <div className="form-check" key={c}>
                <input className="form-check-input" type="checkbox" id={`car-cond-${c}`} checked={!!values[conditionKey(c)]}
                  onChange={e => set(conditionKey(c), e.target.checked || undefined)} />
                <label className="form-check-label small" htmlFor={`car-cond-${c}`}>{c}</label>
              </div>
            ))}
          </div>
          <div className="row g-2 mt-2">
            <div className="col-md-6">
              <label htmlFor="car-decision" className="form-label small mb-1 fw-semibold">11.9 Final recommendation</label>
              <select id="car-decision" className="form-select form-select-sm" value={(values['s11.decision'] as string) || ''}
                onChange={e => set('s11.decision', e.target.value || undefined)}>
                <option value="">Select…</option>
                {DECISIONS.map(d => <option key={d} value={d}>{DECISION_LABELS[d]}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
      {s.calculations && (
        <div className="bg-light rounded p-2 mt-3 small">
          {s.calculations.map(c => {
            const v = c.value(values);
            return (
              <div key={c.label} className="d-flex justify-content-between">
                <span>{c.label}</span>
                <strong className={c.signed && v !== undefined && v < 0 ? 'text-danger' : ''}>{money(v)}</strong>
              </div>
            );
          })}
        </div>
      )}
      {s.outcome && (
        <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
          <span className="small fw-semibold">Assessment outcome</span>
          {OUTCOMES.map(o => (
            <button key={o} type="button"
              className={`btn btn-sm ${values[`${s.key}.outcome`] === o ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => set(`${s.key}.outcome`, values[`${s.key}.outcome`] === o ? undefined : o)}>
              {OUTCOME_LABELS[o]}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (!applicationId) return null;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h6 className="card-title mb-0">Credit Assessment Report</h6>
          <small className="text-muted">
            Tirhelo credit procedure (manual rev01).{' '}
            {report?.exists
              ? `Saved by ${report.updatedBy || 'someone'}${report.updatedAt ? ` · ${formatSouthAfricanDateTime(report.updatedAt)}` : ''}`
              : 'Not saved yet: started from the application.'}
          </small>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="badge bg-light text-dark border">Evidence: {evidence}</span>
          <span className={`badge ${OVERALL_CLASS[result]}`}>{OVERALL_LABELS[result]}</span>
          {values['s11.decision'] && <span className="badge bg-primary">{DECISION_LABELS[values['s11.decision'] as keyof typeof DECISION_LABELS]}</span>}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Hide report' : 'Open report'}
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={save.isPending || isLoading || (!dirty && !!report?.exists)}>
            {save.isPending ? 'Saving…' : dirty ? 'Save report' : 'Saved'}
          </button>
        </div>
      </div>
      {open && (
        <div className="card-body p-0">
          {issues.length > 0 && (
            <div className="alert alert-warning m-3 mb-0 small">
              <div className="fw-semibold mb-1">Decision consistency check</div>
              <ul className="mb-0 ps-3">{issues.map(i => <li key={i}>{i}</li>)}</ul>
            </div>
          )}
          <div className="accordion accordion-flush">
            {SECTIONS.map(s => (
              <div className="accordion-item" key={s.key}>
                <h2 className="accordion-header">
                  <button type="button" className={`accordion-button py-2 ${openSection === s.key ? '' : 'collapsed'}`}
                    onClick={() => setOpenSection(prev => (prev === s.key ? '' : s.key))} aria-expanded={openSection === s.key}>
                    <span className="small fw-semibold">{s.title}</span>{sectionBadge(s)}
                  </button>
                </h2>
                {openSection === s.key && renderSection(s)}
              </div>
            ))}
          </div>
          {dirty && (
            <div className="d-flex justify-content-end p-3 border-top">
              <button type="button" className="btn btn-primary" onClick={onSave} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save report'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * A report started on the earlier template (rev0) keeps opening as it was; everything else uses the credit
 * team's template of 27 Oct 2026, with the existing credit information inside its Section 3.
 */
const CreditAssessmentReport: React.FC<{
  loanApplication: any;
  externalCredit?: React.ReactNode;
  onRecommendationChange?: (r: ReportRecommendation) => void;
}> = ({ loanApplication, externalCredit, onRecommendationChange }) => {
  const applicationId: string = loanApplication?.id || '';
  const { data: report, isLoading } = useGetCreditAssessmentReport(applicationId, { query: { enabled: !!applicationId } });
  if (!applicationId || isLoading) return null;
  if (report?.exists && (report.content as any)?._template !== TEMPLATE_VERSION) {
    return <>{externalCredit}<LegacyCreditAssessmentReport loanApplication={loanApplication} /></>;
  }
  return <CreditAssessmentTemplateReport loanApplication={loanApplication} externalCredit={externalCredit}
    onRecommendationChange={onRecommendationChange} />;
};

export default CreditAssessmentReport;
